import { format, parse, isValid, formatDistanceToNow, parseISO } from "date-fns";
import { formatInTimeZone } from 'date-fns-tz'
import { groupBy, values } from "lodash";

export class DateUtil {
  static todayDay = new Date();
  static firstDayOfYear = new Date(new Date().getFullYear(), 0, 1);
  static firstDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  static lastDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

  //https://stackoverflow.com/questions/64362242/how-to-format-date-with-date-fns
  static dateStringToDate(dateString, dateStringFormat = "yyyy-MM-dd") {
    return parse(dateString, dateStringFormat, new Date());
  } //be careful for format letters

  static formatDate(date, dateStringFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") {
    return format(date, dateStringFormat)
  } //be careful for format letters

  static formatDateUTC(date, dateStringFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") {
    return formatInTimeZone(date, 'UTC', dateStringFormat)
  } //be careful for format letters

  //https://stackoverflow.com/questions/40346773/unique-array-for-dates-javascript
  static filterUniqueDates(data) {
    const lookup = new Set();

    return data.filter((date) => {
      const serialised = date.getTime();
      if (lookup.has(serialised)) {
        return false;
      } else {
        lookup.add(serialised);
        return true;
      }
    });
  }

  //https://stackoverflow.com/questions/1643320/get-month-name-from-date/18648314#18648314
  //It is now possible to do this with the ECMAScript Internationalization API:
  static getMonthLocaleString(date) {
    return date.toLocaleString("default", { month: "long" });
  }

  //https://stackoverflow.com/questions/2013255/how-to-get-year-month-day-from-a-date-object
  static getDMYFromDateAsObject(uniqueDates) {
    const dayMonthYearObj = uniqueDates.map((ud) => ({ day: ud.getDay() + 1, month: ud.getMonth() + 1, year: ud.getFullYear() }));
    return dayMonthYearObj;
  }

  static getMYFromDateAsObject(uniqueDates) {
    const monthYearObj = uniqueDates.map((ud) => ({ month: ud.getMonth() + 1, year: ud.getFullYear() }));
    return monthYearObj;
  }

  //https://www.geeksforgeeks.org/how-to-get-distinct-values-from-an-array-of-objects-in-javascript/
  static getUniqueObjects(objectArray, compareProp) {
    const unique_values = objectArray
      .map((item) => item[compareProp])
      .filter((value, index, current_value) => current_value.indexOf(value) === index);

    return unique_values;
  }

  //https://medium.com/@sonygupta_82335/finding-unique-objects-in-array-based-on-multiple-properties-770eeb91711c
  static getUniqueObjectsByMultipleProps(objectArray = [], propsArray = Object.getOwnPropertyNames(objectArray[0])) {
    const combinationsArray = {};
    const unique_values = objectArray.filter((obj) => {
      let key = propsArray.map((m) => obj[m]).join("|");

      //console.log("getUniqueObjectsMultipleProps", { objectArray, propsArray, key, combinationsArray})
      if (!combinationsArray[key]) {
        combinationsArray[key] = true;
        return true;
      }
      return false;
    }, Object.create(null));

    //console.log("getUniqueObjectsMultipleProps", unique_values)
    return unique_values;
  }

  //https://codingbeautydev.com/blog/javascript-convert-month-number-to-name
  static getMonthNameByNumber(monthNumber) {
    const date = new Date();
    date.setMonth(monthNumber - 1);

    return date.toLocaleString("en-US", { month: "long" });
  }

  //https://codingbeautydev.com/blog/javascript-date-subtract-days/
  static subtractDays(date = new Date(), days) {
    date.setDate(date.getDate() - days);

    return date;
  }

  static filterUniqueDatesFromBackend(vendingData) {
    const uniqueDatesArray = DateUtil.filterUniqueDates(vendingData.map((transaction) => new Date(transaction.date.replaceAll(".", "/"))));
    return uniqueDatesArray;
  }

  // CUSTOM MONTHS SELECTION
  static getUniqueMonthsYears = (uniqueDatesArray = [new Date()], caller = "MONTHS-YEARS") => {
    // MONTHS SELECTION
    //const uniqueDatesArray = DateUtil.filterUniqueDates(transactionsResponseData.map(item => new Date(item.date)))

    /*const uniqueDatesArray = DateUtil.filterUniqueDates(vendingData.map(item => {
                    //console.log("NEW DATE", { d1: item.date, d2: item.date.replaceAll(".", "/") })
                    new Date(item.date.replaceAll(".", "/"))
                }))*/

    const monthYearArray = DateUtil.getMYFromDateAsObject(uniqueDatesArray);
    const monthFilterProps = monthYearArray.length > 0 ? Object.getOwnPropertyNames(monthYearArray[0]) : [];
    const uniqueMonthYearArray = monthYearArray.length > 0 ? DateUtil.getUniqueObjectsByMultipleProps(monthYearArray, monthFilterProps) : [];

    //SORY by MONTH, YEAR [ASC]
    uniqueMonthYearArray.sort((a, b) => a.month - b.month && a.year - b.year);

    //console.log(`[DATE UTILS] ${caller} ====>`, { uniqueDatesArray, monthYearArray, monthFilterProps, uniqueMonthYearArray })

    return uniqueMonthYearArray;
  };

  //https://www.amitmerchant.com/human-readable-date-difference-in-javascript/
  static getTimeDiffInHumanReadableFormat(timestamp) {
    const olderDate = new Date(timestamp);
    const currentDate = new Date();

    //console.log("DATE OLD-NOW", { olderDate, currentDate })

    const diff = olderDate - currentDate;

    const formatter = new Intl.RelativeTimeFormat("en", {
      numeric: "always",
    });

    //console.log("[DATE UTIL] DIFF in HUMAN READABLE FORMAT", formatter.format(Math.round(diff / 86400000), 'day'));
    // 1 day ago

    const diffInDays = Math.round(diff / 86400000);
    //console.log("getTimeDiffInHumanReadableFormat ====> ", {timestamp, olderDate, currentDate, diff, diffInDays})

    return diffInDays > 0 ? formatter.format(diffInDays, "day") : "0 days";
  }

  //Converts YYYY.MM.DD backend date format to YYYY/MM/DD (firefox issue)
  // Fix FIREFOX (firefox) issue
  static convertBackendDateFormat(dateString) {
    return dateString.replaceAll(".", "/");
  }

  /**
   *
   * @param {*} dateString "2023-07-26 ..."
   * @returns "26/07/2023"
   */
  static formatDateFromBackend(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   *
   * @param {Date String} Date
   * @returns Date String
   * @description Convert "YYYY-MM-DD HH:mm:ss.sss" to Backend time string ("dd.MM.yyyy hh:mm:ss") need to ISO convert first
   *
   */
  static convertDateStringToBackendDateString(dateTimeString) {
    const year = dateTimeString && dateTimeString.slice(0, 4);
    const month = dateTimeString && dateTimeString.slice(5, 7);
    const day = dateTimeString && dateTimeString.slice(8, 10);
    const hour = dateTimeString && dateTimeString.slice(11, 19);
    const converted = `${day}.${month}.${year} ${hour}`;
    return converted;
  }

  static convertDateStringToBackendDateStringNoHours(dateTimeString) {
    const year = dateTimeString && dateTimeString.slice(0, 4);
    const month = dateTimeString && dateTimeString.slice(5, 7);
    const day = dateTimeString && dateTimeString.slice(8, 10);
    const converted = `${year}.${month}.${day}`; //'2022.11.30'
    return converted;
  }

  /**
   *
   * @param {String} dateStringWithDots "dd.MM.yyyy hh:mm:ss" 
   * @returns Date String
   * @description Convert Backend time with Dots ("dd.MM.yyyy hh:mm:ss") to "YYYY-MM-DD HH:mm:ss.sss"
   */
  static convertBackendDateDots_to_YYYY_MM_DD(dateStringWithDots) {
    //Convert Backend time string ("dd.MM.yyyy hh:mm:ss") to "YYYY-MM-DD HH:mm:ss.sss"
    const day = dateStringWithDots && dateStringWithDots.slice(0, 2); //'30.11.2022 10:56:50'
    const month = dateStringWithDots && dateStringWithDots.slice(3, 5);
    const year = dateStringWithDots && dateStringWithDots.slice(6, 10);
    const hour = dateStringWithDots && dateStringWithDots.slice(11);

    const converted = new Date(`${year}-${month}-${day} ${hour}`);

    return converted;
  }

  /**
    *
    * @param {Date String} Date
    * @returns Date String
    * @description Convert "YYYY-MM-DD HH:mm:ss.sss" to Backend time string ("dd.MM.yyyy hh:mm:ss") to
    */

  static convertDateStringToBackendDateString(dateTimeString) {
    const year = dateTimeString && dateTimeString.slice(0, 4);
    const month = dateTimeString && dateTimeString.slice(5, 7);
    const day = dateTimeString && dateTimeString.slice(8, 10);
    const hour = dateTimeString && dateTimeString.slice(11, 19);

    const converted = `${day}.${month}.${year} ${hour}`;
    return converted;

  }


  static datesAreOnSameDay = (first, second) =>
    first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();

  //----------------------------------- CGPT
  /**
   *
   * @param {timestamp} start
   * @param {timestamp} end
   * @param {array} data
   * @param {array} channels
   * @returns data array with Filled missing dates with 0 for each payment channel
   */
  static fillMissingDatesWithZeros(start, end, data, channels) {
    if (!data[0]?.date) return data;

    const startDate = new Date(start);
    const endDate = new Date(end) > new Date() ? new Date() : new Date(end);

    //ARE DATES ARE THE SAME DAY?
    //console.log("DATES 1 SAME CHECK ============> ", {startDate, endDate, same: DateUtil.datesAreOnSameDay(startDate, endDate) })

    if (DateUtil.datesAreOnSameDay(startDate, endDate)) return data;

    const channelsZero = channels.reduce((acc, channel) => {
      return { ...acc, [channel]: 0 };
    }, {});
    const filledData = Array.from({ length: (endDate - startDate) / (24 * 60 * 60 * 1000) + 1 }, (_, index) => {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + index);
      const currentDateStr = currentDate.toISOString().slice(0, 10);
      const existingData = data.find((item) => new Date(DateUtil.convertBackendDateFormat(item.date)).toISOString().slice(0, 10) === currentDateStr);

      return existingData ? { ...channelsZero, ...existingData, date: currentDateStr } : { date: currentDateStr, ...channelsZero };
    });

    return filledData;
  }

  /**
   *
   * @param {timestamp} start
   * @param {timestamp} end
   * @param {array} data
   * @returns data array with Filled missing dates with 0 count and 0 amount
   */
  static fillMissingAmountCountDatesWithZeros(start, end, data) {
    if (!data[0]?.date) return data;
    const startDate = new Date(start);
    const endDate = new Date(end) > new Date() ? new Date() : new Date(end);

    //ARE DATES ARE THE SAME DAY?
    //console.log("DATES 2 SAME CHECK ============> ", {startDate, endDate, same: DateUtil.datesAreOnSameDay(startDate, endDate) })
    if (DateUtil.datesAreOnSameDay(startDate, endDate)) return data;

    const filledData = Array.from({ length: (endDate - startDate) / (24 * 60 * 60 * 1000) + 1 }, (_, index) => {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + index);
      const currentDateStr = currentDate.toISOString().slice(0, 10);
      const existingData = data.find((item) => new Date(DateUtil.convertBackendDateFormat(item.date)).toISOString().slice(0, 10) === currentDateStr);

      return existingData ? { ...existingData, date: currentDateStr } : { date: currentDateStr, count: 0, amount: 0 };
    });

    return filledData;
  }
  //----------------------------------- CGPT

  static sortToCorrectWeekDays = (data) => {
    const map = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 7,
    };

    const allWeekDays = Object.keys(map);

    const sortedWeekdays = allWeekDays.map((weekday) => {
      const transactions = data.filter((item) => item.day === weekday);
      return { day: weekday, transactions };
    });

    allWeekDays.forEach((weekday) => {
      if (!sortedWeekdays.some((item) => item.day === weekday)) {
        sortedWeekdays.push({ day: weekday, transactions: [] });
      }
    });

    return sortedWeekdays.sort((a, b) => map[a.day] - map[b.day]);
  };

  static addOneMoreDay = (data) => {
    return data.map((item) => {
      const currentDate = new Date(item.date);
      if (!isNaN(currentDate.getTime())) {
        currentDate.setDate(currentDate.getDate() + 1);
        const formattedDate = currentDate.toISOString().slice(0, 10);
        return formattedDate;
      } else {
        return item.date;
      }
    });
  };

  static sortLastYearToToday = (list) => {
    const todayDate = new Date().getMonth();
    let lastYearList = [];
    let thisYearList = [];
    if (list?.length > 0) {
      list?.map((selectedMonth) => {
        if (selectedMonth.value > todayDate) {
          lastYearList.push(selectedMonth);
        } else {
          thisYearList.push(selectedMonth);
        }
      });

      lastYearList.sort((a, b) => {
        return a.value - b.value;
      });
      thisYearList.sort((a, b) => {
        return a.value - b.value;
      });
      return [...lastYearList, ...thisYearList];
    } else {
      return list;
    }
  }

  static addYearToMonth = (list) => {
    const todayDate = new Date().getMonth();
    return list?.map((item, index) => {
      if (index > todayDate) {
        return `${item}, ${new Date().getFullYear() - 1}`;
      } else {
        return `${item}, ${new Date().getFullYear()}`;
      }
    })
  }

  static whatYearIsTheDate = (list, singleMonth) => {
    const todayDate = new Date().getMonth();
    const theYear = list.filter((month, index) => index === singleMonth)
    if (singleMonth > todayDate) {
      return `${theYear}, ${new Date().getFullYear() - 1}`
    } else {
      return `${theYear}, ${new Date().getFullYear()}`
    }
  }

  //chat gpt 
  // static formatTimeAgo(dateString) {
  //   const date = parseISO(dateString);
  //   const timeAgo = formatDistanceToNow(date, { addSuffix: true });
  //   return timeAgo;
  // }

  static formatTimeAgo(dateString) {
    if (!dateString) {
      // Handle null, undefined, or empty string cases
      return 'Invalid date';
    }
    
    // Parse the ISO date string
    const date = parseISO(dateString);

    // Check if the parsed date is valid
    if (!isValid(date)) {
      return 'Invalid date';
    }

    // Format the distance to now
    const timeAgo = formatDistanceToNow(date, { addSuffix: true });
    return timeAgo;
  }
}
